import React, { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { CalendarClock, Play, Pencil, Trash2, Plus, X } from "lucide-react";
import { ColonyBadge, ColonyDotName } from "@/lib/colonyColors";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const WEEKDAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const EMPTY_FORM = {
  colony_id: "",
  title: "",
  description: "",
  frequency: "daily",
  weekdays: [],
  time_of_day: "06:00",
  assignees: [],
  active: true,
  require_live_photo: false,
};

/**
 * Manage auto-recurring colony tickets: daily/weekly schedules that open
 * a real ticket for a specific colony, assigned ONLY to the chosen
 * worker(s). Skips a run when the previous auto ticket is still open.
 */
export const TicketSchedulesDialog = ({ open, onOpenChange, colonies, eligible }) => {
  const [schedules, setSchedules] = useState([]);
  // Fresh worker list fetched on every open — the parent's copy can go
  // stale if worker logins were changed since the page loaded, which
  // made saves fail on ids that no longer exist.
  const [workers, setWorkers] = useState(eligible || []);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null); // null = list view; object = editing/new
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [colonyFilter, setColonyFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest"); // newest | oldest | colony | title

  const sortedSchedules = React.useMemo(() => {
    const arr = schedules.filter((s) => colonyFilter === "all" || s.colony_id === colonyFilter);
    const byCreatedDesc = (a, b) => (b.created_at || "").localeCompare(a.created_at || "");
    if (sortBy === "oldest") arr.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    else if (sortBy === "colony") arr.sort((a, b) => (a.colony_name || "").localeCompare(b.colony_name || "") || byCreatedDesc(a, b));
    else if (sortBy === "title") arr.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    else if (sortBy === "time") arr.sort((a, b) => (a.time_of_day || "99:99").localeCompare(b.time_of_day || "99:99") || byCreatedDesc(a, b));
    else arr.sort(byCreatedDesc);
    return arr;
  }, [schedules, colonyFilter, sortBy]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, elig] = await Promise.all([
        api.get("/mgmt/ticket-schedules"),
        api.get("/mgmt/eligible-watchers").catch(() => null),
      ]);
      setSchedules(data || []);
      if (elig?.data) setWorkers(elig.data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) { load(); setForm(null); setEditingId(null); }
  }, [open, load]);

  const startNew = () => { setForm({ ...EMPTY_FORM, colony_id: colonies[0]?.id || "" }); setEditingId(null); };
  const startEdit = (s) => {
    const days = (s.weekdays && s.weekdays.length ? s.weekdays : (s.weekday != null ? [s.weekday] : []));
    // Legacy weekly rows saved with multiple days open as Custom.
    const freq = s.frequency === "weekly" && days.length > 1 ? "custom" : s.frequency;
    setForm({
      colony_id: s.colony_id, title: s.title, description: s.description || "",
      frequency: freq,
      weekdays: days,
      time_of_day: s.time_of_day || "06:00",
      assignees: s.assignees || [], active: s.active !== false,
      require_live_photo: s.require_live_photo === true,
    });
    setEditingId(s.id);
  };

  const toggleWeekday = (d) => {
    setForm((f) => ({
      ...f,
      weekdays: f.weekdays.includes(d)
        ? f.weekdays.filter((x) => x !== d)
        : [...f.weekdays, d].sort((a, b) => a - b),
    }));
  };

  const toggleAssignee = (uid) => {
    setForm((f) => ({
      ...f,
      assignees: f.assignees.includes(uid)
        ? f.assignees.filter((a) => a !== uid)
        : [...f.assignees, uid],
    }));
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required.");
    if (!form.colony_id) return toast.error("Pick a colony.");
    if (!form.assignees.length) return toast.error("Pick at least one worker.");
    if (form.frequency !== "daily" && !form.weekdays.length) return toast.error("Pick at least one day of the week.");
    setSaving(true);
    try {
      const payload = {
        ...form,
        weekdays: form.frequency === "daily" ? null
          : form.frequency === "weekly" ? [form.weekdays[0]]
          : form.weekdays,
      };
      if (editingId) {
        const { data } = await api.patch(`/mgmt/ticket-schedules/${editingId}`, payload);
        setSchedules((prev) => prev.map((s) => (s.id === editingId ? data : s)));
        toast.success("Schedule updated");
      } else {
        const { data } = await api.post("/mgmt/ticket-schedules", payload);
        setSchedules((prev) => [data, ...prev]);
        toast.success("Auto ticket schedule created");
      }
      setForm(null); setEditingId(null);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (s) => {
    setBusyId(s.id);
    try {
      const { data } = await api.patch(`/mgmt/ticket-schedules/${s.id}`, { active: !(s.active !== false) });
      setSchedules((prev) => prev.map((x) => (x.id === s.id ? data : x)));
      toast.success(data.active ? "Schedule resumed" : "Schedule paused");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const runNow = async (s) => {
    setBusyId(s.id);
    try {
      const { data } = await api.post(`/mgmt/ticket-schedules/${s.id}/run-now`);
      if (data.created) toast.success("Ticket opened and worker notified");
      else toast.message(data.reason || "Skipped — previous auto ticket is still open.");
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete the "${s.title}" schedule? Existing tickets stay.`)) return;
    setBusyId(s.id);
    try {
      await api.delete(`/mgmt/ticket-schedules/${s.id}`);
      setSchedules((prev) => prev.filter((x) => x.id !== s.id));
      toast.success("Schedule deleted");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const cadenceLabel = (s) => {
    if (s.frequency === "daily") return `Daily (Sun–Fri) at ${s.time_of_day}`;
    const days = (s.weekdays && s.weekdays.length ? s.weekdays : (s.weekday != null ? [s.weekday] : []));
    if (s.frequency === "weekly" && days.length <= 1) {
      return `Weekly · ${WEEKDAYS[days[0] ?? 0]} at ${s.time_of_day}`;
    }
    return `Custom · ${days.map((d) => WEEKDAY_SHORT[d]).join(", ") || "—"} at ${s.time_of_day}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ticket-schedules-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-5 h-5" /> Auto tickets
          </DialogTitle>
          <DialogDescription>
            Recurring tickets open automatically (Eastern time) for a colony and go only to the
            worker(s) you assign. A run is skipped if the previous auto ticket is still open.
          </DialogDescription>
        </DialogHeader>

        {!form && (
          <div className="space-y-3">
            <Button size="sm" className="rounded-sm" onClick={startNew} data-testid="schedule-new-btn">
              <Plus className="w-3.5 h-3.5 mr-2" /> New schedule
            </Button>
            {schedules.length > 1 && (
              <div className="flex items-end gap-3 flex-wrap">
                <div className="min-w-[200px]">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Colony</div>
                  <Select value={colonyFilter} onValueChange={setColonyFilter}>
                    <SelectTrigger className="h-9 rounded-sm" data-testid="schedules-colony-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All colonies</SelectItem>
                      {colonies.map((c) => (
                        <SelectItem key={c.id} value={c.id}><ColonyDotName id={c.id} name={c.name} /></SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="min-w-[170px]">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Sort by</div>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="h-9 rounded-sm" data-testid="schedules-sort">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">Newest first</SelectItem>
                      <SelectItem value="oldest">Oldest first</SelectItem>
                      <SelectItem value="colony">Bungalow colony</SelectItem>
                      <SelectItem value="title">Title</SelectItem>
                      <SelectItem value="time">Schedule time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {!loading && !schedules.length && (
              <p className="text-sm text-muted-foreground" data-testid="schedules-empty">
                No auto ticket schedules yet.
              </p>
            )}
            {sortedSchedules.map((s) => (
              <div key={s.id} className="border border-border rounded-sm p-3 space-y-1" data-testid={`schedule-row-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold text-sm">{s.title}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
                      <ColonyBadge id={s.colony_id} name={s.colony_name} /> <span>{cadenceLabel(s)}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Assigned to: {(s.assignee_names || []).join(", ") || "—"}
                    </div>
                    {s.require_live_photo && (
                      <span className="inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 mt-1 rounded-sm border border-emerald-400 bg-emerald-50 text-emerald-800 font-bold" data-testid={`schedule-live-required-${s.id}`}>
                        Live photo required
                      </span>
                    )}
                    {s.last_run_on && (
                      <div className="text-[11px] text-muted-foreground">
                        Last run {s.last_run_on} — {s.last_run_result === "created" ? "ticket opened" : "skipped (previous still open)"}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Switch
                      checked={s.active !== false}
                      onCheckedChange={() => toggleActive(s)}
                      disabled={busyId === s.id}
                      data-testid={`schedule-active-${s.id}`}
                    />
                  </div>
                </div>
                <div className="flex gap-1 pt-1">
                  <Button variant="outline" size="sm" className="rounded-sm h-7 text-xs" onClick={() => runNow(s)} disabled={busyId === s.id} data-testid={`schedule-run-${s.id}`}>
                    <Play className="w-3 h-3 mr-1" /> Run now
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-sm h-7 text-xs" onClick={() => startEdit(s)} data-testid={`schedule-edit-${s.id}`}>
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-sm h-7 text-xs text-red-600" onClick={() => remove(s)} disabled={busyId === s.id} data-testid={`schedule-delete-${s.id}`}>
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {form && (
          <div className="space-y-3" data-testid="schedule-form">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">{editingId ? "Edit schedule" : "New schedule"}</p>
              <Button variant="ghost" size="sm" className="h-7" onClick={() => { setForm(null); setEditingId(null); }} data-testid="schedule-form-cancel">
                <X className="w-3.5 h-3.5 mr-1" /> Cancel
              </Button>
            </div>
            <div>
              <Label className="text-xs">Colony</Label>
              <Select value={form.colony_id} onValueChange={(v) => setForm((f) => ({ ...f, colony_id: v }))}>
                <SelectTrigger className="h-9 rounded-sm" data-testid="schedule-colony-select"><SelectValue placeholder="Pick a colony" /></SelectTrigger>
                <SelectContent>
                  {colonies.map((c) => <SelectItem key={c.id} value={c.id}><ColonyDotName id={c.id} name={c.name} /></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Ticket title</Label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Morning pool check" className="rounded-sm" data-testid="schedule-title-input" />
            </div>
            <div>
              <Label className="text-xs">Description (optional)</Label>
              <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} className="rounded-sm" data-testid="schedule-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Repeats</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm((f) => ({ ...f, frequency: v }))}>
                  <SelectTrigger className="h-9 rounded-sm" data-testid="schedule-frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily (Sun–Fri, no Saturday)</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="custom">Custom days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Time (Eastern)</Label>
                <Input type="time" value={form.time_of_day} onChange={(e) => setForm((f) => ({ ...f, time_of_day: e.target.value }))} className="rounded-sm h-9" data-testid="schedule-time-input" />
              </div>
            </div>
            {form.frequency === "weekly" && (
              <div>
                <Label className="text-xs">Day of the week</Label>
                <Select
                  value={form.weekdays.length ? String(form.weekdays[0]) : ""}
                  onValueChange={(v) => setForm((f) => ({ ...f, weekdays: [Number(v)] }))}
                >
                  <SelectTrigger className="h-9 rounded-sm" data-testid="schedule-weekday-select">
                    <SelectValue placeholder="Pick a day" />
                  </SelectTrigger>
                  <SelectContent>
                    {WEEKDAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.frequency === "custom" && (
              <div>
                <Label className="text-xs">On these days (pick any)</Label>
                <div className="flex flex-wrap gap-1 mt-1" data-testid="schedule-weekday-picker">
                  {WEEKDAY_SHORT.map((d, i) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => toggleWeekday(i)}
                      className={`px-2.5 py-1.5 text-xs font-semibold rounded-sm border transition ${
                        form.weekdays.includes(i)
                          ? "bg-emerald-700 text-white border-emerald-700"
                          : "bg-background text-muted-foreground border-border hover:border-emerald-600"
                      }`}
                      data-testid={`schedule-weekday-${i}`}
                      title={WEEKDAYS[i]}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs">Assign to (only these workers see the ticket)</Label>
              <div className="border border-border rounded-sm p-2 max-h-40 overflow-y-auto space-y-1 mt-1">
                {!workers.length && <p className="text-xs text-muted-foreground">No workers available yet.</p>}
                {workers.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`schedule-assignee-${u.id}`}>
                    <Checkbox checked={form.assignees.includes(u.id)} onCheckedChange={() => toggleAssignee(u.id)} />
                    <span>{u.name || u.email}</span>
                    {u.role === "driver" && <span className="text-[10px] text-muted-foreground">(driver + worker)</span>}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-start justify-between gap-3 border border-border rounded-sm p-3">
              <div>
                <Label className="text-xs font-semibold">Require a Live photo to close</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  The worker can&apos;t close this ticket until it has at least one Live/Motion photo
                  (taken with the LIVE camera button).
                </p>
              </div>
              <Switch
                checked={form.require_live_photo}
                onCheckedChange={(v) => setForm((f) => ({ ...f, require_live_photo: v }))}
                data-testid="schedule-require-live-toggle"
              />
            </div>
            <Button onClick={save} disabled={saving} className="rounded-sm w-full" data-testid="schedule-save-btn">
              {saving ? "Saving…" : editingId ? "Save changes" : "Create schedule"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
